import { useMemo, useState } from 'react';
import { useHashView } from './useHashView';
import { signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './firebase';
import { Sidebar, type View } from './components/Sidebar';
import { SummaryCards } from './components/SummaryCards';
import { TargetTable } from './components/TargetTable';
import { HistoryTable } from './components/HistoryTable';
import { Anomalies } from './components/Anomalies';
import { ClientsReview, DuplicateClients } from './clients';
import { Policies } from './policies';
import { TeamMembers } from './components/TeamMembers';
import { BrandingProvider } from './branding';
import { KnowledgeBase } from './components/KnowledgeBase';
import { SourceSection } from './components/SourceSection';
import { Settings, HiddenPanel } from './components/Settings';
import { AlertSettings } from './components/AlertSettings';
import { AlertHealthBanner } from './components/AlertHealthBanner';
import { BriefArchive } from './components/BriefArchive';
import { MaintenanceWindows } from './components/MaintenanceWindows';
import { Help } from './help';
import { BriefPanel } from './brief';
import { Dashboard } from './dashboard';
import { Roles } from './roles';
import { Wordmark } from './brand';
import { useLogoState } from './branding';
import { ClientUsers } from './components/ClientUsers';
import { useTargets, useSummary, useStatusChanges } from './hooks/useBackupData';
import { type Source, type Status } from './types';
import { partition, countsBySource } from './visibility';
import { HideDialog, type HideRequest } from './components/HideDialog';
import { IntegrationsPanel } from './integrations';
import { ReportsPanel } from './reports';
import { ChangesPanel } from './changes';
import { MentionsPanel } from './mentions';
import { MentionBell } from './components/MentionBell';
import type { Target } from './types';
import { useOrg, useCan } from './org';
import { FeatureRequests } from './components/FeatureRequests';
import { AiPanel } from './components/AiPanel';

const SOURCE_VIEWS: readonly View[] = ['datto', 'sp', 'sc', 'msp', 'unitrends', 'email'];
const isSourceView = (v: View): v is Source => SOURCE_VIEWS.includes(v);

/**
 * Order of the stacked sections on the Overview, matching the original PHP
 * dashboard: Datto, then ShadowProtect, then Image Manager.
 *
 * Kept as a single array so making it user-arrangeable later is a matter of
 * persisting a reordered copy of this - nothing else needs to know the order.
 */
const SECTION_ORDER: readonly Source[] = ['datto', 'sp', 'sc', 'msp', 'unitrends', 'email'];

/**
 * The product mark in the page header - shown only when it would not be a
 * duplicate.
 *
 * A SEPARATE COMPONENT because BrandingProvider is mounted inside App's own
 * JSX. A hook called in App's body sits ABOVE the provider and reads its
 * default value, which is empty branding - so every tenant would look like they
 * had no logo. Rendering here puts the hook underneath the provider, where the
 * answer is real.
 *
 * When a tenant has no logo of their own the sidebar is already showing this
 * exact lockup, and the same mark twice reads as a layout mistake rather than
 * as branding.
 */
function HeaderMark() {
  const { isDefault } = useLogoState();
  if (isDefault) return null;
  return <h1 className="product-name"><Wordmark width={196} /></h1>;
}
export function App({ email }: { email: string | null }) {
  const [view, setView] = useHashView<View>(
    ['overview', 'datto', 'sp', 'sc', 'msp', 'unitrends', 'email', 'history', 'hidden', 'anomalies', 'knowledge', 'team',
      'policies', 'alerts', 'integrations', 'settings', 'help', 'wallboard', 'roles', 'clientusers', 'changes', 'mentions'],
    'overview');
  const [filter, setFilter] = useState<Status | 'all'>('all');
  const [search, setSearch] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  // One place decides which tenant is in view; every read below is scoped to it.
  const { current: org } = useOrg();
  const orgId = org?.orgId ?? null;
  /*
   * ONE CAPABILITY PER QUESTION.
   *
   * This used to be a single `canHide = role is owner or admin`, handed to
   * eleven different panels as `canManage`. So the permission to hide a machine
   * also decided branding, the web address, two-factor policy, the team list,
   * policies and alerts - and a custom role holding any of those could not see
   * the page at all, because the check never looked past two role names.
   *
   * Damon, 2026-08-10: "in settings a tech should not have access to branding,
   * web address, 2fa setup."
   */
  const can = useCan();
  const canHide = can('targets.hide');

  /*
   * Whether the Configuration group appears at all. Any ONE of these is enough
   * - the group is a container, and hiding it because somebody lacks the other
   * eight would hide the page they do have.
   */
  const canConfigure =
    can('members.manage') || can('org.manage') || can('policies.manage') ||
    can('alerts.manage') || can('integrations.manage') || can('clients.portal') ||
    can('branding.manage') || can('domain.manage') || can('security.manage') ||
    can('ticketing.manage');

  const { targets, loading, error } = useTargets(orgId);
  const summary = useSummary(orgId);
  const changes = useStatusChanges(orgId);

  // Excluded once, at the top, so the tables, the cards and the sidebar badges
  // can never disagree about which machines exist. See visibility.ts.
  const { visible: shown, hidden } = useMemo(() => partition(targets), [targets]);
  const counts = useMemo(() => countsBySource(shown), [shown]);

  const scoped = useMemo(
    () => (isSourceView(view) ? shown.filter((t) => t.source === view) : shown),
    [shown, view]
  );

  const viewCounts = isSourceView(view) ? counts.bySource[view] : counts.overall;

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (t: (typeof shown)[number]) => {
      if (filter !== 'all' && t.status !== filter) return false;
      if (!q) return true;
      return t.name.toLowerCase().includes(q) || (t.client ?? '').toLowerCase().includes(q);
    };
  }, [filter, search]);

  const rows = useMemo(() => scoped.filter(matches), [scoped, matches]);
  const isFiltered = filter !== 'all' || search.trim() !== '';

  const [hideTarget, setHideTarget] = useState<Target | null>(null);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  async function applyHide(req: HideRequest) {
    setHideTarget(null);
    if (!orgId) return;
    try {
      await httpsCallable(functions, 'setTargetHidden')({
        orgId, targetId: req.target.id, hidden: true,
        reason: req.reason, until: req.until,
      });
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Could not hide that machine.');
    }
  }

  const doSignOut = () => void signOut(auth);

  async function syncNow() {
    setSyncing(true);
    setSyncError(null);
    try {
      if (!orgId) throw new Error('No organisation selected.');
      await httpsCallable(functions, 'syncNow')({ orgId, source: 'all' });
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  }

  const isOverview = view === 'overview';
  /**
   * True only for the views that ARE the machine list: the Overview and the
   * per-source pages.
   *
   * This was a deny-list - "not history, not settings, not integrations" - and
   * that shape fails open. Adding Hidden Endpoints and Schedule review without
   * also remembering to extend the list rendered the entire Overview, summary
   * cards and all, underneath both panels. An allow-list cannot fail that way:
   * a new panel view shows its panel and nothing else, by default.
   *
   * It also gates the search box, which has nothing to search on a panel page.
   */
  const showsData = isOverview || isSourceView(view);
  const failed = viewCounts.failed;

  return (
    <BrandingProvider orgId={orgId}>
    <div className="layout">
      <Sidebar
        view={view}
        onNavigate={(v) => { setView(v); setFilter('all'); }}
        counts={counts}
        summary={summary}
        email={email}
        isManager={canConfigure}
          canViewHidden={can('targets.viewHidden')}
          canViewHistory={can('history.view')}
          canViewChanges={can('changes.view')}
          canViewWallboard={can('wallboard.view')}
        onSignOut={doSignOut}
        onSyncNow={() => void syncNow()}
        onFeatureRequest={() => setFeaturesOpen(true)}
        onAi={() => setAiOpen(true)}
        syncing={syncing}
      />

      <main className="main">
        <header className="page-head">
          <div className="page-title-wrap">
              {/* No page title and no tenant pill: the sidebar already says
                  which page you are on, and a single-tenant user does not need
                  reminding which tenant they are. */}
              <HeaderMark />
            </div>
          <div className="head-right">
            {showsData && (
              <input
                className="search"
                type="search"
                placeholder="Search machines or clients…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            )}
            {/* A sidebar entry tells you a page EXISTS; it does not tell you
                something is waiting on it, and nobody opens a page on the off
                chance a colleague needed them. The count has to come to you. */}
            <MentionBell orgId={orgId ?? null} onOpen={() => setView('mentions')} />
          </div>
        </header>

        {error && <div className="banner banner-error">Could not load data: {error}</div>}
        {syncError && <div className="banner banner-error">{syncError}</div>}

        {view === 'settings' && (
          <Settings
            summary={summary}
            counts={counts}
            email={email}
            orgId={orgId}
            canBranding={can('branding.manage')}
            canDomain={can('domain.manage')}
            canSecurity={can('security.manage')}
            onSignOut={doSignOut}
          />
        )}

        {view === 'history' && can('history.view') && <HistoryTable changes={changes} />}

        {view === 'hidden' && can('targets.viewHidden') && (
            <HiddenPanel hidden={hidden} orgId={orgId} canManage={can('targets.hide')} />
          )}

        {view === 'anomalies' && (
          <>
            {orgId && <ClientsReview orgId={orgId} canManage={can('anomalies.resolve')} />}
              {orgId && <DuplicateClients orgId={orgId} canManage={can('anomalies.resolve')} />}
            <Anomalies orgId={orgId} canManage={can('anomalies.resolve')} />
          </>
        )}

        {view === 'knowledge' && <KnowledgeBase orgId={orgId} canManage={can('knowledge.write')} />}

        {view === 'team' && <TeamMembers orgId={orgId} canManage={can('members.manage')} />}

        {view === 'roles' && <Roles orgId={orgId} canManage={can('org.manage')} />}

        {view === 'clientusers' && <ClientUsers orgId={orgId} canManage={can('clients.portal')} />}

        {view === 'policies' && <Policies orgId={orgId} canManage={can('policies.manage')} />}

        {view === 'alerts' && (
          <>
            <AlertSettings orgId={orgId} canManage={can('alerts.manage')} />
            {/* Beside alerting rather than in Settings: a window is a statement
                about when alerts are wanted, so it belongs with the alerts. */}
            <MaintenanceWindows orgId={orgId} canManage={can('alerts.manage')} />
          </>
        )}

        {view === 'help' && <Help />}

        {/* The hash is user input. Hiding the nav item is not enough - somebody
            who bookmarked #wallboard before the plan changed would still land
            on it. */}
        {view === 'wallboard' && can('wallboard.view') && (
          <Dashboard targets={shown} counts={counts} summary={summary} changes={changes as never} />
        )}

        {view === 'integrations' && orgId && <IntegrationsPanel orgId={orgId} />}
        {view === 'reports' && orgId && <ReportsPanel orgId={orgId} />}
      {view === 'changes' && orgId && <ChangesPanel orgId={orgId} />}
      {view === 'mentions' && orgId && <MentionsPanel orgId={orgId} />}

        {/* ABOVE the attention banner and above the cards, on the Overview only.
            It is the first thing read, and it is about the interval between
            visits rather than the current moment - which is exactly what the
            cards below cannot tell you. Dismissed, it does not come back until
            something else changes. */}
        {/* ABOVE the brief and outside the isOverview guard on purpose. The
            brief is about what changed; this is about the product being unable
            to tell you anything at all, which is worth saying on whichever
            page somebody happens to be looking at. */}
        <AlertHealthBanner orgId={orgId} canManage={can('alerts.manage')} />

        {isOverview && <BriefPanel orgId={orgId} />}
        {/* Under the brief, and collapsed. It answers a question people ask
            occasionally; the brief above answers the one they came for. */}
        {isOverview && <BriefArchive orgId={orgId} />}

        {showsData && (
          <>
            {failed > 0 && (
              <div className="banner banner-alert">
                {failed} {failed === 1 ? 'machine requires' : 'machines require'} immediate attention.
              </div>
            )}

            <SummaryCards counts={viewCounts} active={filter} onFilter={setFilter} />

            {loading ? (
              <div className="empty">Loading…</div>
            ) : isOverview ? (
              // Stacked per-source sections, the way the original dashboard read.
              SECTION_ORDER.map((s) => (
                <SourceSection
                  key={s}
                  source={s}
                  targets={shown.filter((t) => t.source === s).filter(matches)}
                  orgId={orgId}
                  counts={counts.bySource[s]}
                  filtered={isFiltered}
                  canHide={canHide}
                  onHide={setHideTarget}
                  onOpenSource={(src) => { setView(src); setFilter('all'); }}
                />
              ))
            ) : (
              <TargetTable
                source={rows[0]?.source}
                orgId={orgId}
                targets={rows}
                canHide={canHide}
                onHide={setHideTarget}
                emptyMessage={
                  filter === 'all'
                    ? 'No machines reported for this source yet.'
                    : `No machines are currently ${filter}.`
                }
              />
            )}
          </>
        )}
      </main>

      {aiOpen && (
        <AiPanel
          orgId={orgId}
          view={view}
          targets={shown}
          counts={counts}
          summary={summary}
          onClose={() => setAiOpen(false)}
        />
      )}

      {featuresOpen && (
        <FeatureRequests
          orgId={orgId}
          view={view}
          version={__APP_VERSION__}
          onClose={() => setFeaturesOpen(false)}
        />
      )}

      {hideTarget && (
        <HideDialog
          target={hideTarget}
          onCancel={() => setHideTarget(null)}
          onConfirm={(req) => void applyHide(req)}
        />
      )}
    </div>
    </BrandingProvider>
  );
}

